import * as React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  BarChart3,
  FileText,
  Bell,
  Search,
  Landmark,
  RefreshCw,
  Flag,
  LayoutGrid,
} from "lucide-react";
import { NetWorthGlyph, CashFlowGlyph, BuyingPowerGlyph, DebtToIncomeGlyph } from "./appCustomIcons";
import { AppTopBar } from "./AppShellChrome";

const TABS = [
  { icon: LayoutGrid, label: "Overview", active: true },
  { icon: NetWorthGlyph, label: "Net Worth" },
  { icon: CashFlowGlyph, label: "Cash Flow" },
  { icon: BuyingPowerGlyph, label: "Buying Power" },
  { icon: DebtToIncomeGlyph, label: "Debt-to-Income" },
  { icon: Flag, label: "Milestones" },
];

const TabsRow: React.FC = () => (
  <div className="mb-1 flex items-center gap-2 border-b border-gray-200 overflow-x-auto no-scrollbar">
    {TABS.map((t) => (
      <div
        key={t.label}
        className={`flex items-center gap-1 pb-0.5 shrink-0 whitespace-nowrap text-[6.5px] font-semibold border-b-2 ${
          t.active ? "border-blue-600 text-blue-600" : "border-transparent text-gray-400"
        }`}
      >
        <t.icon className="w-2 h-2" />
        {t.label}
      </div>
    ))}
  </div>
);

const WealthCard: React.FC<{
  icon: React.ElementType;
  title: string;
  value: React.ReactNode;
  sub: string;
  status: string;
  statusColor: string;
  delay: number;
}> = ({ icon: CardIcon, title, value, sub, status, statusColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="rounded-md border border-gray-200 bg-white p-1"
  >
    <div className="mb-0.5 flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-blue-100 flex items-center justify-center shrink-0">
        <CardIcon className="w-1.5 h-1.5 text-blue-600" />
      </div>
      <p className="text-gray-600 text-[6px] font-medium truncate">{title}</p>
    </div>
    <p className="font-bold text-gray-900 text-[8.5px]">{value}</p>
    <p className="text-gray-400 text-[5.5px] mt-0.5 truncate">{sub}</p>
    <div className="mt-0.5 flex items-center gap-1">
      <span className={`w-1 h-1 rounded-full ${statusColor}`} />
      <span className={`text-[5.5px] font-semibold ${statusColor.replace("bg-", "text-")}`}>{status}</span>
    </div>
  </motion.div>
);

/* ─── WealthScope module screen — mirrors the real Wealth Overview page ── */
export const WealthScopeDashboardMock: React.FC = () => {
  return (
    <div className="h-full w-full bg-slate-50 px-2.5 sm:px-4 py-2 overflow-hidden text-[11px] sm:text-xs">
      <AppTopBar />
      <TabsRow />

      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[9.5px] sm:text-[10px] font-bold truncate">
            <span className="text-blue-600">Hi User, </span>
            <span className="text-gray-900">Welcome to Wealth Overview</span>
          </p>
          <span className="mt-0.5 flex items-center gap-1 text-gray-400 text-[6.5px]">
            Updated just now
            <RefreshCw className="w-1.5 h-1.5" />
          </span>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-blue-600 px-1.5 py-0.5 text-white font-semibold shrink-0">
          <Landmark className="w-2 h-2" />
          <span className="text-[6.5px]">Add bank account</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1">
        <WealthCard
          icon={NetWorthGlyph}
          title="Net Worth"
          value="$3,842,650"
          sub="Complete financial overview"
          status="+612.4%"
          statusColor="bg-emerald-500"
          delay={0.1}
        />
        <WealthCard
          icon={CashFlowGlyph}
          title="Monthly Cash Flow"
          value="$14,320"
          sub="Income minus tracked expenses"
          status="+38.7%"
          statusColor="bg-emerald-500"
          delay={0.18}
        />
        <WealthCard
          icon={BuyingPowerGlyph}
          title="Buying Power"
          value="$912,400"
          sub="Investment capacity"
          status="Ready"
          statusColor="bg-emerald-500"
          delay={0.26}
        />
        <WealthCard
          icon={DebtToIncomeGlyph}
          title="Debt-to-Income"
          value="8.9%"
          sub="Risk assessment"
          status="Healthy"
          statusColor="bg-emerald-500"
          delay={0.34}
        />
        <WealthCard
          icon={FileText}
          title="Reports & Data"
          value="35 Reports"
          sub="WealthScope reports & data"
          status="Up to date"
          statusColor="bg-emerald-500"
          delay={0.42}
        />
      </div>
    </div>
  );
};

const PORTFOLIOIQ_TABS = [
  { icon: LayoutGrid, label: "Overview", active: true },
  { icon: Building2, label: "Properties" },
  { icon: BarChart3, label: "Performance" },
  { icon: Search, label: "Entities" },
  { icon: FileText, label: "Review" },
  { icon: Flag, label: "Lease Calendar" },
];

/* ─── PortfolioIQ module screen — mirrors the real Portfolio IQ page ── */
export const PortfolioIQDashboardMock: React.FC = () => {
  return (
    <div className="h-full w-full bg-slate-50 px-2.5 sm:px-4 py-2.5 sm:py-3 overflow-hidden text-[11px] sm:text-xs">
      <AppTopBar />
      <div className="mb-2 flex items-center gap-2.5 border-b border-gray-200 overflow-x-auto no-scrollbar">
        {PORTFOLIOIQ_TABS.map((t) => (
          <div
            key={t.label}
            className={`flex items-center gap-1 pb-1 shrink-0 whitespace-nowrap text-[7px] font-semibold border-b-2 ${
              t.active ? "border-blue-600 text-blue-600" : "border-transparent text-gray-400"
            }`}
          >
            <t.icon className="w-2.5 h-2.5" />
            {t.label}
          </div>
        ))}
      </div>

      <div className="mb-2 min-w-0">
        <p className="text-[10px] sm:text-[11px] font-bold truncate">
          <span className="text-blue-600">Hi User, </span>
          <span className="text-gray-900">Welcome to Portfolio IQ</span>
        </p>
        <span className="mt-0.5 flex items-center gap-1 text-gray-400 text-[7px]">
          Updated just now
          <RefreshCw className="w-2 h-2" />
        </span>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <WealthCard
          icon={Building2}
          title="Properties"
          value="14"
          sub="Manage your real estate portfolio"
          status="All performing"
          statusColor="bg-emerald-500"
          delay={0.1}
        />
        <WealthCard
          icon={BarChart3}
          title="Performance"
          value="94/100"
          sub="View analytics and insights"
          status="Strong"
          statusColor="bg-emerald-500"
          delay={0.18}
        />
        <WealthCard
          icon={FileText}
          title="Reports & Data"
          value="37 Reports"
          sub="Export reports and view data"
          status="Up to date"
          statusColor="bg-emerald-500"
          delay={0.26}
        />
        <WealthCard
          icon={Bell}
          title="Alerts & Insights"
          value="4"
          sub="Important updates and notifications"
          status="Reviewed"
          statusColor="bg-emerald-500"
          delay={0.34}
        />
      </div>
    </div>
  );
};
